/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { task } from 'ember-concurrency';
import { action } from '@ember/object';
import FormValidations from 'cio/utils/form-validations';
import { isBlank } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import LoginService, { LoginError } from 'cio/services/login';
import AccountService from 'cio/services/account';
import AuthService from 'cio/services/auth';
import AnalyticsService from 'cio/services/analytics';
import AlertsService from 'cio/services/alerts';

interface PasswordModalArgs {
  isVisible: boolean;
}

const Validations = {
  password(value: string) {
    if (isBlank(value)) {
      return 'Please enter your password';
    }

    return true;
  },
};

export default class PasswordModal extends Component<PasswordModalArgs> {
  @service login!: LoginService;
  @service account!: AccountService;
  @service auth!: AuthService;
  @service analytics!: AnalyticsService;
  @service alerts!: AlertsService;

  private validations!: FormValidations;

  @tracked show = false;

  @tracked formValues = {
    password: '',
  };

  constructor(owner: unknown, args: PasswordModalArgs) {
    super(owner, args);

    this.validations = FormValidations.create({
      validations: Validations,
      context: this,
      modelKey: 'formValues',
    });
  }

  submitTask = task(async (event: Event) => {
    event.preventDefault();

    try {
      await this.validations.validateModel();
    } catch (e) {
      // `validateModel` already found and set validation errors
      // return early to avoid executing real submit logic
      return;
    }
    this.alerts.clearAlerts();
    let { currentUser } = this.account.current;
    let data = { email: currentUser.email, password: this.formValues.password };
    let twoFactorAuthEnabled =
      (currentUser.twoFactorAuthenticationEnabled ?? false) ||
      (currentUser.twoFactorAuthenticationEmailEnabled ?? false);
    await this.login.passwordReAuthenticate.perform(data, twoFactorAuthEnabled).catch((err) => {
      if (err instanceof LoginError) {
        this.alerts.error('reauth.error', err.message);
      }
    });
  });

  @action
  showForgotPasswordPopover(popoverOpen: () => unknown) {
    popoverOpen();
    this.analytics.track('user', 'forgot_password_clicked', { source: 're-authentication modal' });
  }

  @action
  onPasswordInput(value: string) {
    this.formValues = { ...this.formValues, password: value };
    this.validations.validateAttribute('password');
  }

  @action
  clearPassword() {
    this.formValues = { ...this.formValues, password: '' };
  }

  @action
  logout() {
    this.auth.logout();
  }

  @action
  noop() {}
}
